import { formatCurrency } from '@mahawi/eshop-common/dist/src/format-currency';
import { round } from '@mahawi/eshop-common/dist/src/round';
import {
  EEventContextAction,
  EEventType,
  type IBasketProduct,
  type ICountryCurrencyPrice,
  type ICurrencyPrice,
  type ICustomerCurrencyType,
  type ITranslation,
} from '@mahawi/eshop-common/dist/src/types';
import { type Dispatch } from '@reduxjs/toolkit';
import React, { type ReactElement, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import { addProduct, removeProduct } from '../../reducers/basket/reducer';
import { type IBasketState } from '../../reducers/basket/types';
import { type ICurrencyState } from '../../reducers/currency/types';
import { eventSend } from '../../reducers/event/reducer';
import { type ILanguageState } from '../../reducers/language/types';
import { load } from '../../reducers/product/reducer';
import { type IUserState } from '../../reducers/user/types';
import { type ITagManager } from '../../types';

function OrderTable({
  Basket,
  companyHasPricesWithoutTax,
  Currency,
  dispatch,
  getItemsForTagManager,
  Language,
  TagManager,
  User,
}: {
  Basket: IBasketState;
  companyHasPricesWithoutTax: boolean;
  Currency: ICurrencyState;
  dispatch: Dispatch;
  getItemsForTagManager;
  Language: ILanguageState;
  TagManager?: ITagManager;
  User: IUserState;
}): ReactElement {
  const { t } = useTranslation();

  const languageCodeLower: string = useMemo(
    (): string => Language.languageType.code.toLowerCase(),
    [Language.languageType.code],
  );

  const currency: ICustomerCurrencyType | undefined = useMemo(
    (): ICustomerCurrencyType | undefined =>
      Currency.currencies.find(
        (c: ICustomerCurrencyType): boolean =>
          c.isoCode === User.currencyType?.isoCode,
      ),
    [Currency.currencies, User.currencyType],
  );

  const changeProductCount = useCallback(
    (productUUID: string, count: number): void => {
      dispatch(
        addProduct({
          product: { uuid: productUUID },
          count,
          discount: {
            code: Basket.discount.code,
          },
        }),
      );

      dispatch(
        eventSend({
          action: EEventContextAction.CHANGE,
          type: EEventType.PAGE_BASKET_CHANGE_PRODUCT_COUNT,
          context: {
            product: { uuid: productUUID },
            count,
          },
        }),
      );

      if (TagManager) {
        const items = getItemsForTagManager();
        TagManager.dataLayer({
          dataLayer: {
            event: 'add_to_cart',
            ecommerce: {
              items,
            },
          },
        });
      }
    },
    [dispatch, Basket.discount.code, TagManager, getItemsForTagManager],
  );

  const handleRemoveProduct = useCallback(
    (productUUID: string) => {
      dispatch(
        removeProduct({
          product: { uuid: productUUID },
          discount: {
            code: Basket.discount.code,
          },
        }),
      );
      dispatch(
        eventSend({
          action: EEventContextAction.REMOVE,
          type: EEventType.PRODUCT_REMOVED_FROM_BASKET,
          context: { product: { uuid: productUUID } },
        }),
      );
    },
    [dispatch, Basket.discount.code],
  );

  const handleLoadProduct = useCallback(
    (productUUID: string) => {
      dispatch(
        load({
          product: {
            uuid: productUUID,
          },
        }),
      );
    },
    [dispatch],
  );

  return (
    <table className="orderTable">
      <thead>
        <tr>
          <th>{t('basket.productName')}</th>
          <th>{t('basket.count')}</th>
          <th>{t('basket.pricePerUnit')}</th>
          <th>{t('basket.totalPrice')}</th>
          <th>{t('basket.totalWeight')}</th>
          <th>{t('basket.action')}</th>
        </tr>
      </thead>
      <tbody>
        {Basket.products.map((product: IBasketProduct): ReactElement | null => {
          const productPrice: ICurrencyPrice | undefined =
            product.currencyPrices.find(
              (c: ICurrencyPrice): boolean =>
                c.isoCode === User.currencyType?.isoCode,
            );

          if (!productPrice) return null;

          const { pricesWithTax, priceWithoutTax } = productPrice;

          const priceWithTax: ICountryCurrencyPrice | undefined =
            pricesWithTax.find(
              (p: ICountryCurrencyPrice): boolean =>
                p.isoCode === (User.countryType?.isoCode || 'CZE'),
            );

          const priceToShow: number = companyHasPricesWithoutTax
            ? priceWithoutTax
            : priceWithTax?.price || 0;

          const slug: ITranslation | undefined = product.slugs.find(
            ({ code }: ITranslation): boolean =>
              code === Language.languageType.code,
          );

          const link: string = slug?.value
            ? `/${languageCodeLower}/p/${slug.value}`
            : `/${languageCodeLower}/product/${product.uuid}`;

          const productName: string =
            product.names.find(
              (n: ITranslation): boolean =>
                n.code === Language.languageType.code,
            )?.value || '';

          return (
            <tr key={product.uuid}>
              <td>
                <Link to={link}>
                  <button
                    type="button"
                    onClick={(): void => handleLoadProduct(product.uuid)}
                  >
                    {productName}
                  </button>
                </Link>
              </td>
              <td>
                <button
                  type="button"
                  className="arrow"
                  onClick={(): void => changeProductCount(product.uuid, -1)}
                  disabled={product.count <= 1}
                  aria-label="Decrease product count"
                >
                  {product.count > 1 && (
                    <svg className="arrowDownOutlined">
                      <path d="M12 21.312l-7.121-7.121c-1.17-1.17-1.17-3.073 0-4.242 1.094-1.094 2.978-1.138 4.121-.115v-4.834c0-1.654 1.346-3 3-3s3 1.346 3 3v4.834c1.143-1.023 3.027-.979 4.121.115 1.17 1.169 1.17 3.072 0 4.242l-7.121 7.121zm-5-10.242c-.268 0-.518.104-.707.293-.391.39-.391 1.023 0 1.414l5.707 5.707 5.707-5.707c.391-.391.391-1.024 0-1.414-.379-.379-1.035-.379-1.414 0l-3.293 3.293v-9.656c0-.551-.448-1-1-1s-1 .449-1 1v9.656l-3.293-3.293c-.189-.189-.439-.293-.707-.293z" />
                    </svg>
                  )}
                </button>
                {product.count}
                <button
                  type="button"
                  className="arrow"
                  onClick={(): void => changeProductCount(product.uuid, 1)}
                  aria-label="Increase product count"
                >
                  <svg className="arrowUpOutlined">
                    <path d="M12 21c-1.654 0-3-1.346-3-3v-4.764c-1.143 1.024-3.025.979-4.121-.115-1.17-1.169-1.17-3.073 0-4.242l7.121-7.121 7.121 7.121c1.17 1.169 1.17 3.073 0 4.242-1.094 1.095-2.979 1.14-4.121.115v4.764c0 1.654-1.346 3-3 3zm-1-12.586v9.586c0 .551.448 1 1 1s1-.449 1-1v-9.586l3.293 3.293c.379.378 1.035.378 1.414 0 .391-.391.391-1.023 0-1.414l-5.707-5.707-5.707 5.707c-.391.391-.391 1.023 0 1.414.379.378 1.035.378 1.414 0l3.293-3.293z" />
                  </svg>
                </button>
              </td>
              <td>{formatCurrency(priceToShow, currency)}</td>
              <td>
                {currency
                  ? formatCurrency(priceToShow * product.count, currency)
                  : round(priceToShow * product.count, 2)}
              </td>
              <td>
                {`${round(product.dimensions.weight * product.count, 4)} kg`}
              </td>
              <td>
                <button
                  type="button"
                  className="action"
                  onClick={(): void => handleRemoveProduct(product.uuid)}
                  aria-label="Remove product from basket"
                >
                  <svg className="deleteOutlined">
                    <path d="M16 9v10H8V9h8m-1.5-6h-5l-1 1H5v2h14V4h-3.5l-1-1zM18 7H6v12c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7z" />
                  </svg>
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

const mapStateToProps = ({ Basket, User, Language, Currency }) => ({
  Basket,
  User,
  Language,
  Currency,
});

export default connect(mapStateToProps)(OrderTable);
